/**
 * CostingPrintDocument — aperçu imprimable (PDF) + export HTML autonome du pack
 * Costing (Labour cost · Capacity cost · Cost assignment).
 *
 * mode='pdf'  : overlay d'aperçu + bouton Imprimer (→ PDF via dialogue navigateur)
 * mode='html' : capture offscreen → fichier .html autonome → download → onClose
 *
 * Source de contenu unique : CostingPrintTables (réutilisé dans les 2 modes).
 */
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useP } from '../../i18n/useP';
import CostingPrintTables from './CostingPrintTables';
import '../hr/PeoplePrintDocument.css';

const YEAR = new Date().getFullYear();

function inlineAllStyles() {
  const chunks = [];
  for (const sheet of Array.from(document.styleSheets)) {
    try {
      const rules = sheet.cssRules;
      if (!rules) continue;
      for (const rule of Array.from(rules)) chunks.push(rule.cssText);
    } catch (_) { /* CORS — skip */ }
  }
  return chunks.join('\n');
}

export default function CostingPrintDocument({ activeTenant, year = YEAR, mode = 'pdf', onClose }) {
  const { t } = useTranslation();
  const p = useP();
  const [ready, setReady] = useState(false);
  const printRef = useRef(null);
  const editedAt = useState(() => new Date())[0];

  const footerLeft = `${(activeTenant?.name || '').toUpperCase()} · ${editedAt.toLocaleDateString('fr-CH', { day: '2-digit', month: '2-digit', year: 'numeric' })}`;
  const cssVarStyle = { '--ppl-footer-left': `"${footerLeft.replace(/"/g, '\\"')}"` };

  useEffect(() => {
    document.body.classList.add('people-printing');
    return () => document.body.classList.remove('people-printing');
  }, []);

  useEffect(() => {
    const handler = () => onClose && onClose();
    window.addEventListener('afterprint', handler);
    return () => window.removeEventListener('afterprint', handler);
  }, [onClose]);

  // Mode HTML : dès que les données sont prêtes, capturer + télécharger + fermer.
  useEffect(() => {
    if (mode !== 'html' || !ready) return;
    const el = printRef.current;
    if (!el) return;
    const css = inlineAllStyles();
    const title = `Palestre Costing — ${activeTenant?.name || ''} — ${year}`;
    const doc = `<!DOCTYPE html><html lang="fr"><head><meta charset="utf-8">
<title>${title}</title><style>${css}
  body{background:#fff;margin:0;padding:24px;}
  .ppl-print-overlay,.ppl-print-controls{position:static!important;background:#fff!important;}
  .ppl-print-controls{display:none!important;}
  .ppl-print__preview{background:#fff!important;padding:0!important;}
</style></head><body><div class="ppl-print">${el.innerHTML}</div></body></html>`;
    const blob = new Blob([doc], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Palestre_Costing_${activeTenant?.name || 'tenant'}_${year}.html`.replace(/\s+/g, '_');
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    const t = setTimeout(() => onClose && onClose(), 200);
    return () => clearTimeout(t);
  }, [mode, ready]); // eslint-disable-line

  const handlePrint = () => window.print();

  return (
    <div className="ppl-print-overlay" style={cssVarStyle}>
      {mode === 'pdf' && (
        <div className="ppl-print-controls">
          <div className="ppl-print-controls__left">
            <span className="ppl-print-controls__title">{p('Aperçu — Costing · ')}{activeTenant?.name}</span>
            {!ready && <span className="ppl-print-controls__loading">⟳ {t('common:status.loading')}</span>}
          </div>
          <div className="ppl-print-controls__right">
            <button className="ppl-print-btn ppl-print-btn--print" onClick={handlePrint} disabled={!ready}
                    title={ready ? p('Imprimer ou sauvegarder en PDF') : p('Patientez…')}>
              {ready ? `⎙ ${p('Imprimer / PDF')}` : `⟳ ${t('common:status.loading')}`}
            </button>
            <button className="ppl-print-btn ppl-print-btn--close" onClick={onClose}>✕ {t('common:actions.close')}</button>
          </div>
        </div>
      )}
      <div className="ppl-print__preview">
        <div className="ppl-print" ref={printRef}>
          <CostingPrintTables activeTenant={activeTenant} year={year} editedAt={editedAt} onDataReady={() => setReady(true)} />
        </div>
      </div>
    </div>
  );
}
